import { useState, Dispatch, SetStateAction } from 'react';
import { useRouter } from 'next/router';
import cn from 'classnames';
// Types
import { GetAllAmas_amas_filters } from 'api/ama/types/GetAllAmas';
import { GetStores_stores_filters } from 'api/store/types/GetStores';
import { GetStreams_streamsV2_filtersV2 } from 'api/streams/types/GetStreams';
import { GetMerchProducts_getMerchProducts_filters } from 'api/merch/types/GetMerchProducts';
import { GetMemorabilia_getMemorabilia_filters } from 'api/memorabilia/types/GetMemorabilia';
import { StreamType } from 'api/graphql-global-types';
import { SortKey } from 'components/common3/Sorting/Sorting';
import { STREAM_STATUS_FILTER_VALUES } from 'helpers/streamFilters';
import { ExtraStoreFilters } from '../../PageWithFiltersWrapper';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Ui3
import Button from 'ui3/Button/Button';
import Loader from 'ui3/Loader/Loader';
import Text from 'ui3/Text/Text';
// Components
import GenderFilter from './components/GenderFilter/GenderFilter';
import ProfileTypeFilter from './components/ProfileTypeFilter/ProfileTypeFilter';
import AthletesFilter from './components/AthletesFilter/AthletesFilter';
import OrganizationsFilter from './components/OrganizationsFilter/OrganizationsFilter';
import ContentCreatorsFilter from './components/ContentCreatorsFilter/ContentCreatorsFilter';
import ResponseDayFilter from './components/ResponseDayFilter/ResponseDayFilter';
import PriceFilter from './components/PriceFilter/PriceFilter';
import AthleteSportFilter from './components/AthleteSportFilter/AthleteSportFilter';
import MerchTypeFilter from './components/MerchTypeFilter/MerchTypeFilter';
import SizeFilter from './components/SizeFilter/SizeFilter';
import ColorFilter from './components/ColorFilter/ColorFilter';
import DateFilter from './components/DateFilter/DateFilter';
import TagsFilter from './components/TagsFilter/TagsFilter';
import StreamStatusFilter from './components/StreamStatusFilter/StreamStatusFilter';
import MemorabiliaProductTypeFilter from './components/MemorabiliaProductTypeFilter/MemorabiliaProductTypeFilter';
import SaleMethodFilter from './components/SaleMethodFilter/SaleMethodFilter';
import ProductTypeFilter from './components/ProductTypeFilter/ProductTypeFilter';
import Sorting from 'components/common3/Sorting/Sorting';
import StreamTypeFilter from './components/StreamTypeFilter/StreamTypeFilter';
import WatchTypePills, {
  WATCH_TYPE_INTERVIEWS,
} from './components/WatchTypePills/WatchTypePills';
// Styles
import styles from './Filters.module.scss';

type AllFilters =
  | Omit<GetAllAmas_amas_filters, '__typename'>
  | Omit<GetStores_stores_filters, '__typename'>
  | Omit<GetStreams_streamsV2_filtersV2, '__typename'>
  | Omit<GetMerchProducts_getMerchProducts_filters, '__typename'>
  | Omit<GetMemorabilia_getMemorabilia_filters, '__typename'>
  | Omit<ExtraStoreFilters, '__typename'>;

type FiltersProps = {
  inModal?: boolean;
  loading?: boolean;
  setFiltersModalIsVisible?: Dispatch<SetStateAction<boolean>>;
  filters?: Partial<AllFilters> | null | undefined;
  sortKey?: SortKey;
  noClear?: boolean;
};

const Filters = ({
  inModal,
  loading,
  setFiltersModalIsVisible,
  filters: initialFilters,
  sortKey,
  noClear = false,
}: FiltersProps) => {
  const { query, pathname } = useRouter();

  const isCreatorPage = pathname.includes('/creators');

  const {
    clearFilters,
    hideStoreFilter = true,
    hideContentCreatorsFilter,
    hideOrganizationsFilter,
    hideAthletesFilter,
    hideSports,
    hideProductType,
    sorting,
    setSorting,
    filters: contextFilters,
  } = useFiltersContext();

  const [datePickerVisible, setDatePickerVisible] = useState<boolean>(false);

  const filters = initialFilters || {};

  const allEmpty = Object.keys(contextFilters).every(function (key) {
    return contextFilters[key].length === 0;
  });

  const setDefaultFiltersState = () => {
    if ('scheduleDate' in filters && filters?.scheduleDate) {
      setDatePickerVisible(false);
    }

    if (setFiltersModalIsVisible) {
      setFiltersModalIsVisible(false);
    }
  };

  const onFilterClear = () => {
    clearFilters();
    setDefaultFiltersState();
  };

  const onModalApplyClick = () => {
    if (setFiltersModalIsVisible) {
      setFiltersModalIsVisible(false);
    }
  };

  const hasPriceInFilters = 'price' in filters && Boolean(filters?.price);

  const isOnlyInterviewsWatchType =
    contextFilters.watchType?.length === 1 &&
    contextFilters.watchType?.[0] === WATCH_TYPE_INTERVIEWS;

  const showPriceFilterInStreams =
    hasPriceInFilters && !isOnlyInterviewsWatchType;

  return (
    <div className={cn(styles.root, { [styles.rootInModal]: inModal })}>
      <Text variant="h5" className={inModal ? styles.modalTitle : undefined}>
        Filters
      </Text>
      <div className={cn(styles.filtersBody, { [styles.inModal]: inModal })}>
        {sortKey === 'Streams' && (
          <>
            <WatchTypePills inModal={inModal} />
            {showPriceFilterInStreams && filters.price && (
              <PriceFilter availableRange={filters.price} inModal={inModal} />
            )}
            {'streamStatusV2' in filters && filters?.streamStatusV2 && (
              <StreamStatusFilter
                availableOptions={[...STREAM_STATUS_FILTER_VALUES]}
              />
            )}
          </>
        )}

        {inModal && sortKey && (
          <Sorting
            wrapperClassName={styles.sectionSorting}
            sortKey={sortKey}
            sorting={sorting}
            setSorting={setSorting}
            inModal
          />
        )}

        {inModal && loading && <Loader loading size="xLarge" />}

        <>
          {'streamStatusV2' in filters &&
            filters?.streamStatusV2 &&
            sortKey !== 'Streams' && (
              <StreamStatusFilter
                availableOptions={[...STREAM_STATUS_FILTER_VALUES]}
              />
            )}

          {'streamType' in filters &&
            filters?.streamType &&
            sortKey !== 'Streams' && (
              <StreamTypeFilter
                availableOptions={[StreamType.Interview, StreamType.Stream]}
              />
            )}

          {'genderV2' in filters && filters?.genderV2 && (
            <GenderFilter availableOptions={filters?.genderV2} />
          )}

          {hasPriceInFilters && sortKey !== 'Streams' && filters.price && (
            <PriceFilter availableRange={filters.price} inModal={inModal} />
          )}

          {!hideProductType && 'types' in filters && filters?.types && (
            <MemorabiliaProductTypeFilter availableOptions={filters?.types} />
          )}

          {'saleMethods' in filters && filters?.saleMethods && (
            <SaleMethodFilter availableOptions={filters.saleMethods} />
          )}

          {'size' in filters && filters?.size && (
            <SizeFilter availableOptions={filters?.size} />
          )}

          {'dateToResponse' in filters && filters?.dateToResponse && (
            <ResponseDayFilter availableRange={filters.dateToResponse} />
          )}

          {!hideStoreFilter && (
            <ProductTypeFilter
              isStreamer={query.isStreamer === 'true'}
              isShoppingEnabled={query.isShoppingEnabled === 'true'}
            />
          )}

          {isCreatorPage && (
            <ProfileTypeFilter
              isOrganization={query.isOrganization === 'true'}
              isContentCreator={query.isContentCreator === 'true'}
            />
          )}

          {!hideSports && 'sports' in filters && filters?.sports && (
            <AthleteSportFilter availableOptions={filters?.sports} />
          )}

          {!hideAthletesFilter && !loading && <AthletesFilter />}

          {!hideOrganizationsFilter && !loading && <OrganizationsFilter />}

          {!hideContentCreatorsFilter && !loading && <ContentCreatorsFilter />}

          {'hashtags' in filters && filters?.hashtags && (
            <TagsFilter availableOptions={filters?.hashtags} />
          )}

          {'type' in filters && filters?.type && (
            <MerchTypeFilter availableOptions={filters?.type} />
          )}

          {'color' in filters && filters?.color && (
            <ColorFilter availableOptions={filters?.color} />
          )}

          {'scheduleDate' in filters && filters?.scheduleDate && (
            <div
              className={cn(styles.datePickerWrapper, {
                [styles.datePickerWrapperOpen]: datePickerVisible,
              })}
            >
              {/*TODO: hide until TimeZoneFilter feature will be ready*/}
              {/*<TimeZoneFilter />*/}
              <DateFilter
                availableRange={filters?.scheduleDate}
                datePickerVisible={datePickerVisible}
                setDatePickerVisible={setDatePickerVisible}
                inModal={inModal}
              />
            </div>
          )}
        </>
      </div>

      <div
        className={cn(
          styles.filtersFooter,
          inModal && styles.filtersFooterModal
        )}
      >
        {!allEmpty && !noClear && (
          <Button
            variant="secondary"
            className={styles.filtersFooterButton}
            onClick={onFilterClear}
          >
            Clear
          </Button>
        )}
        {inModal && (
          <Button
            className={styles.filtersFooterButton}
            onClick={onModalApplyClick}
          >
            Apply
          </Button>
        )}
      </div>
    </div>
  );
};

export default Filters;
